/**
 * Staging a folder-tree upload in the browser (storage-and-uploads.md §4): picked folders and
 * loose files become one declaration of relative paths under the object prefix. Pure; no React.
 */

export interface TreeFile {
  path: string;
  size: number;
  file: File;
}
export interface StagedRoot {
  name: string;
  kind: 'folder' | 'file';
  files: TreeFile[];
  bytes: number;
}
export interface Staged {
  roots: StagedRoot[];
}
export interface DeclaredFile {
  path: string;
  size_bytes: number;
}

export const MAX_PATH_BYTES = 1024;
const JUNK_NAMES = new Set(['Thumbs.db', 'desktop.ini']);
const TEXT_ENCODER = new TextEncoder();

/** Hidden files and OS droppings are never part of a delivery. */
export function isJunk(path: string): boolean {
  const segs = path.split('/');
  return segs.some((s) => s.startsWith('.')) || JUNK_NAMES.has(segs[segs.length - 1]);
}

/** The same rule the backend enforces (`intake/tree.py`). */
export function pathOk(path: string): boolean {
  if (!path || path.includes('\\') || TEXT_ENCODER.encode(path).length > MAX_PATH_BYTES) return false;
  return path.split('/').every((s) => s !== '' && s !== '.' && s !== '..');
}

export class DuplicateRootError extends Error {
  constructor(public rootName: string) {
    super(`"${rootName}" is already staged`);
    this.name = 'DuplicateRootError';
  }
}

function makeRoot(name: string, kind: StagedRoot['kind'], files: TreeFile[]): StagedRoot {
  return { name, kind, files, bytes: files.reduce((n, f) => n + f.size, 0) };
}

/** A `webkitdirectory` pick: every file's `webkitRelativePath` starts with the folder's name. */
export function rootFromDirectoryInput(files: File[]): StagedRoot | null {
  const firstFile = files.find((f) => f.webkitRelativePath);
  if (!firstFile) return null;
  const name = firstFile.webkitRelativePath.split('/')[0];
  const kept = files.filter(
    (f) => f.webkitRelativePath && f.webkitRelativePath.startsWith(name + '/') && !isJunk(f.webkitRelativePath),
  );
  if (kept.length === 0) return null;
  const treeFiles = kept.map((f) => ({ path: f.webkitRelativePath, size: f.size, file: f }));
  treeFiles.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  return makeRoot(name, 'folder', treeFiles);
}

export function rootFromFile(file: File): StagedRoot {
  const files = isJunk(file.name) ? [] : [{ path: file.name, size: file.size, file }];
  return makeRoot(file.name, 'file', files);
}

export function addRoot(staged: Staged, root: StagedRoot): Staged {
  if (root.files.length === 0) return staged;
  if (staged.roots.some((r) => r.name === root.name)) throw new DuplicateRootError(root.name);
  return { roots: [...staged.roots, root] };
}

export function removeRoot(staged: Staged, name: string): Staged {
  return { roots: staged.roots.filter((r) => r.name !== name) };
}

export function stagedFiles(staged: Staged): TreeFile[] {
  return staged.roots.flatMap((r) => r.files);
}

export function totals(staged: Staged): { fileCount: number; totalBytes: number } {
  const files = stagedFiles(staged);
  return { fileCount: files.length, totalBytes: files.reduce((n, f) => n + f.size, 0) };
}

export function declaration(staged: Staged): DeclaredFile[] {
  return stagedFiles(staged).map((f) => ({ path: f.path, size_bytes: f.size }));
}

/** What a picked root was, kept so a resume can say what to pick again. */
export interface RootSummary {
  name: string;
  kind: 'folder' | 'file';
  fileCount: number;
  bytes: number;
}

export function summarize(staged: Staged): RootSummary[] {
  return staged.roots.map((r) => ({ name: r.name, kind: r.kind, fileCount: r.files.length, bytes: r.bytes }));
}

export interface RootDiff {
  missing: string[];
  extra: string[];
  /** Same name, different file count or size. */
  changed: string[];
}

export function compareRoots(expected: RootSummary[], staged: Staged): RootDiff {
  const picked = new Map(summarize(staged).map((r) => [r.name, r]));
  const wanted = new Set(expected.map((r) => r.name));
  return {
    missing: expected.filter((r) => !picked.has(r.name)).map((r) => r.name),
    extra: [...picked.keys()].filter((n) => !wanted.has(n)),
    changed: expected
      .filter((r) => {
        const p = picked.get(r.name);
        return p !== undefined && (p.fileCount !== r.fileCount || p.bytes !== r.bytes);
      })
      .map((r) => r.name),
  };
}

export const sameRoots = (d: RootDiff) => d.missing.length + d.extra.length + d.changed.length === 0;

export function invalidPaths(staged: Staged): string[] {
  return stagedFiles(staged).filter((f) => !pathOk(f.path)).map((f) => f.path);
}

/** One loose file alone is today's single-file upload; anything else is a tree. */
export function shapeOf(staged: Staged): 'empty' | 'file' | 'tree' {
  if (staged.roots.length === 0) return 'empty';
  return staged.roots.length === 1 && staged.roots[0].kind === 'file' ? 'file' : 'tree';
}

// Drag and drop walks the File System Entry API; typed minimally so tests can fake it.
export interface FileEntryLike {
  isFile: true;
  isDirectory: false;
  name: string;
  file(ok: (f: File) => void, err?: (e: unknown) => void): void;
}
export interface DirEntryLike {
  isFile: false;
  isDirectory: true;
  name: string;
  createReader(): { readEntries(ok: (entries: EntryLike[]) => void, err?: (e: unknown) => void): void };
}
export type EntryLike = FileEntryLike | DirEntryLike;

async function readAll(dir: DirEntryLike): Promise<EntryLike[]> {
  const reader = dir.createReader();
  const out: EntryLike[] = [];
  for (;;) {
    // readEntries pages (100 at a time in Chrome) and signals the end with an empty page.
    const page = await new Promise<EntryLike[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (page.length === 0) return out;
    out.push(...page);
  }
}

export async function walkEntry(entry: EntryLike, prefix = ''): Promise<TreeFile[]> {
  const path = prefix + entry.name;
  if (isJunk(path)) return [];
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject));
    return [{ path, size: file.size, file }];
  }
  const out: TreeFile[] = [];
  for (const child of await readAll(entry)) {
    const sub = await walkEntry(child, path + '/');
    for (const f of sub) out.push(f);
  }
  return out;
}

export async function rootFromEntry(entry: EntryLike): Promise<StagedRoot> {
  const files = await walkEntry(entry);
  files.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  return makeRoot(entry.name, entry.isDirectory ? 'folder' : 'file', files);
}

/** Every dropped item that is a file or a directory; text and links are ignored. */
export async function rootsFromDataTransfer(items: DataTransferItemList): Promise<StagedRoot[]> {
  const entries = Array.from(items)
    .map((i) => i.webkitGetAsEntry() as EntryLike | null)
    .filter((e): e is EntryLike => e !== null);
  const roots = await Promise.all(entries.map(rootFromEntry));
  return roots.filter((r) => r.files.length > 0);
}
